import { APIRoutes } from '@/services/api.routes';
import { APIRequestError } from '@/services/api';
import type { UserID, SubscriptionType } from '@/models';
import type { CreateSubscriptionResponse } from '@/services/api.models';

/**
 * Legt eine Stripe-Checkout-Session für das gegebene Paket an und liefert die
 * Backend-Antwort (Subscription + `checkoutUrl`) zurück, ohne weiterzuleiten.
 * Die Weiterleitung übernimmt der verkettete Checkout in `checkoutChain.ts`, der
 * die Subscription-ID vor dem Redirect für die Ergebnis-Seiten festhält.
 *
 * Schlägt der Versuch MIT Coupon aus einem nicht-"bereits abonniert"-Grund fehl
 * (z.B. ungültiger Coupon), wird einmal OHNE Coupon retried, damit der User
 * trotzdem zum Checkout gelangt. Wirft `APIRequestError` bei endgültigem
 * Fehlschlag – der Aufrufer ist für das Nutzer-Feedback zuständig.
 *
 * `referrerCode` (optional, aus einem `?referrer=`-Link) wird bei jedem Versuch
 * unverändert mitgeschickt, damit die Empfehlung auch beim Coupon-Retry erhalten bleibt.
 */
export async function createCheckoutSession(
  userId: UserID,
  plan: SubscriptionType,
  coupon: string | undefined,
  referrerCode: string | undefined
): Promise<CreateSubscriptionResponse> {
  const attempt = (couponToUse: string | undefined) =>
    APIRoutes.createSubscription(userId, {
      items: [{ subscriptionType: plan }],
      coupon: couponToUse,
      referrerCode: referrerCode
    });

  try {
    return await attempt(coupon);
  } catch (error) {
    // Kein Coupon gesetzt -> nichts, worauf wir zurückfallen könnten
    if (coupon === undefined) {
      throw error;
    }
    // "Bereits abonniert" ist kein Coupon-Problem -> nicht ohne Coupon retrien
    if (error instanceof APIRequestError && error.errorcode === 'SUBSCRIPTION_ALREADY_EXISTS') {
      throw error;
    }
    // Coupon vermutlich ungültig -> einmal ohne Coupon zum Checkout weiterleiten
    console.error('Checkout with coupon failed, retrying without coupon:', error);
    return await attempt(undefined);
  }
}
